import React from "react";
import { Field, FormikErrors, FormikTouched } from "formik";
import { Col, Row } from "react-bootstrap";
import { AddressFormSection, AddressLookupProvider, AddressPanel } from "../../components/UI";
import { RequiredFieldMarker } from "../../components/Forms/RequiredFieldMarker";
import { useViewport } from "../../contexts";
import { RegLookupSelect, type RegLookupSelectItem } from "../../components/Forms/RegLookupSelect";
import type { SiteCreatePayload } from "../../customTypings/site/Site";

type FormValues = SiteCreatePayload & { id?: string };

export interface SiteEditFormGridProps {
  values: FormValues;
  errors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
  handleChange: React.ComponentProps<typeof Field>["onChange"];
  setFieldValue: (field: string, value: unknown) => void;
  setFieldError: (field: string, message: string) => void;
  setFieldTouched: (field: string, touched: boolean) => void;
  setStatus: (status: string) => void;
  showErr: (field: keyof FormValues) => boolean;
  metaDeleted: boolean;
  isNew: boolean;
  facilityOperationItems: RegLookupSelectItem[];
}

function SiteNameField({ showErr, metaDeleted, isNew, errors }: SiteEditFormGridProps) {
  return (
    <>
      <label className="form-label" htmlFor="siteName">
        Site name <RequiredFieldMarker />
      </label>
      <Field
        id="siteName"
        name="siteName"
        className={`form-control${showErr("siteName") ? " is-invalid" : ""}`}
        disabled={metaDeleted}
        aria-required
        aria-invalid={showErr("siteName")}
      />
      {showErr("siteName") && <div className="invalid-feedback d-block">{errors.siteName}</div>}
    </>
  );
}

function EnvironmentalPermitField({ metaDeleted }: Pick<SiteEditFormGridProps, "metaDeleted">) {
  return (
    <>
      <label className="form-label" htmlFor="environmentalPermitNumber">
        Environmental permit number
      </label>
      <Field
        id="environmentalPermitNumber"
        name="environmentalPermitNumber"
        className="form-control"
        disabled={metaDeleted}
      />
    </>
  );
}

function FacilityOperationField({
  metaDeleted,
  values,
  setFieldValue,
  facilityOperationItems,
}: Pick<SiteEditFormGridProps, "metaDeleted" | "values" | "setFieldValue" | "facilityOperationItems">) {
  return (
    <>
      <label className="form-label" htmlFor="regFacilityOperationId">
        Facility or Operation
      </label>
      <RegLookupSelect
        inputId="regFacilityOperationId"
        value={values.regFacilityOperationId ?? ""}
        onChange={(value) => setFieldValue("regFacilityOperationId", value || null)}
        items={facilityOperationItems}
        placeholder="Select facility or operation…"
        isClearable
        fillContainer
        invalid={false}
        disabled={metaDeleted}
      />
    </>
  );
}

function SiteOperatorNameField({ metaDeleted }: Pick<SiteEditFormGridProps, "metaDeleted">) {
  return (
    <>
      <label className="form-label" htmlFor="siteOperatorName">
        Site operator name
      </label>
      <Field id="siteOperatorName" name="siteOperatorName" className="form-control" disabled={metaDeleted} />
    </>
  );
}

function SiteCheckboxFields({ metaDeleted, stacked }: { metaDeleted: boolean; stacked?: boolean }) {
  const checks = (
    <>
      <div className="form-check">
        <Field
          id="weighbridgeUsedFlag"
          name="weighbridgeUsedFlag"
          type="checkbox"
          className="form-check-input"
          disabled={metaDeleted}
        />
        <label className="form-check-label" htmlFor="weighbridgeUsedFlag">
          Weighbridge used
        </label>
      </div>
      <div className="form-check">
        <Field
          id="reportingActiveFlag"
          name="reportingActiveFlag"
          type="checkbox"
          className="form-check-input"
          disabled={metaDeleted}
        />
        <label className="form-check-label" htmlFor="reportingActiveFlag">
          Reporting active
        </label>
      </div>
    </>
  );

  if (stacked) {
    return <div className="d-flex flex-column gap-2 mb-3">{checks}</div>;
  }

  return <div className="d-flex flex-wrap gap-3">{checks}</div>;
}

/** Consistent vertical spacing for each field within lg/md grid cells. */
function GridFieldCell({ children }: { children: React.ReactNode }) {
  return <div className="mb-3">{children}</div>;
}

/** Large screens: left admin stack + right address panel. */
function SiteEditFormGridLarge(props: SiteEditFormGridProps) {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col lg={4}>
          <div className="vstack gap-2">
            <GridFieldCell>
              <SiteNameField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <EnvironmentalPermitField metaDeleted={props.metaDeleted} />
            </GridFieldCell>
            <GridFieldCell>
              <FacilityOperationField
                metaDeleted={props.metaDeleted}
                values={props.values}
                setFieldValue={props.setFieldValue}
                facilityOperationItems={props.facilityOperationItems}
              />
            </GridFieldCell>
            <GridFieldCell>
              <SiteOperatorNameField metaDeleted={props.metaDeleted} />
            </GridFieldCell>
          </div>
        </Col>
        <Col lg={8}>
          <AddressPanel className="mb-3">
            <AddressFormSection
              layoutVariant="pairedRows"
              postcodeLayout="below"
              requiredFields={["addressLine1", "postcode"]}
            />
          </AddressPanel>
        </Col>
      </Row>

      <Row className="g-3 align-items-center">
        <Col lg={8}>
          <GridFieldCell>
            <SiteCheckboxFields metaDeleted={props.metaDeleted} />
          </GridFieldCell>
        </Col>
        <Col lg={4} aria-hidden="true" />
      </Row>
    </div>
  );
}

/** Medium screens: left admin stack + right address panel. */
function SiteEditFormGridMedium(props: SiteEditFormGridProps) {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col md={6}>
          <div className="vstack gap-2">
            <GridFieldCell>
              <SiteNameField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <EnvironmentalPermitField metaDeleted={props.metaDeleted} />
            </GridFieldCell>
            <GridFieldCell>
              <FacilityOperationField
                metaDeleted={props.metaDeleted}
                values={props.values}
                setFieldValue={props.setFieldValue}
                facilityOperationItems={props.facilityOperationItems}
              />
            </GridFieldCell>
            <GridFieldCell>
              <SiteOperatorNameField metaDeleted={props.metaDeleted} />
            </GridFieldCell>
            <GridFieldCell>
              <SiteCheckboxFields metaDeleted={props.metaDeleted} />
            </GridFieldCell>
          </div>
        </Col>
        <Col md={6}>
          <AddressPanel className="mb-3">
            <AddressFormSection
              layoutVariant="stacked"
              postcodeLayout="below"
              requiredFields={["addressLine1", "postcode"]}
            />
          </AddressPanel>
        </Col>
      </Row>
    </div>
  );
}

/** Narrow screens: single column, explicit stack order. */
function SiteEditFormGridNarrow(props: SiteEditFormGridProps) {
  return (
    <>
      <div className="mb-3">
        <SiteNameField {...props} />
      </div>

      <AddressPanel className="mb-3">
        <AddressFormSection
          layoutVariant="stacked"
          postcodeLayout="separate"
          requiredFields={["addressLine1", "postcode"]}
        />
      </AddressPanel>

      <div className="mb-3">
        <EnvironmentalPermitField metaDeleted={props.metaDeleted} />
      </div>
      <div className="mb-3">
        <FacilityOperationField
          metaDeleted={props.metaDeleted}
          values={props.values}
          setFieldValue={props.setFieldValue}
          facilityOperationItems={props.facilityOperationItems}
        />
      </div>
      <div className="mb-3">
        <SiteOperatorNameField metaDeleted={props.metaDeleted} />
      </div>

      <SiteCheckboxFields metaDeleted={props.metaDeleted} stacked />
    </>
  );
}

export function SiteEditFormGrid(props: SiteEditFormGridProps) {
  const { width } = useViewport();

  return (
    <AddressLookupProvider>
      {width >= 992 ? (
        <SiteEditFormGridLarge {...props} />
      ) : width >= 768 ? (
        <SiteEditFormGridMedium {...props} />
      ) : (
        <SiteEditFormGridNarrow {...props} />
      )}
    </AddressLookupProvider>
  );
}
